import { useCallback, useEffect, useState } from "react";

import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import { SmtWelcomeModalDelayMs } from "@/constants/SMT";
import { useModalTracker } from "@/hooks/useModalTracker";
import { MmkvStorage } from "@/hooks/useUnencryptedStorage";
import { useAppSelector } from "@/store/hooks";
import { useAppUserSelector } from "@/store/reducers/appUser";
import { selectWaitingRoomData } from "@/store/reducers/modals/waitingRoomModal";
import { getUserInteractionStorageKey } from "@/utils/smt.util";

interface UseSMTWelcomeModalProps {
  /**
   * Whether the screen using this modal is currently focused.
   * Modal will only show when the screen is focused to prevent
   * showing on background screens in tab navigator.
   */
  isScreenFocused: boolean;
}

const useSMTWelcomeModal = ({ isScreenFocused }: UseSMTWelcomeModalProps) => {
  const [shouldShowWelcomeModal, setShouldShowWelcomeModal] =
    useState<boolean>(false);
  const [isCheckingStoredValue, setIsCheckingStoredValue] =
    useState<boolean>(true);

  const { externalId: appUserId } = useAppUserSelector();
  const waitingRoomData = useAppSelector(selectWaitingRoomData);
  const { isSafeToRenderNativeModals } = useModalTracker();

  // Check if user has interacted with SMT feature before or not
  const checkHasUserInteracted = useCallback(() => {
    if (isEmpty(appUserId)) {
      setIsCheckingStoredValue(false);
      return false;
    }

    const storageKey = getUserInteractionStorageKey(appUserId);
    const storedValue = MmkvStorage.getString(storageKey);
    setIsCheckingStoredValue(false);
    return storedValue === "true";
  }, [appUserId]);

  const markUserAsInteracted = useCallback(() => {
    if (isEmpty(appUserId)) return;

    const storageKey = getUserInteractionStorageKey(appUserId);
    MmkvStorage.set(storageKey, "true");
    setShouldShowWelcomeModal(false);
  }, [appUserId]);

  useEffect(() => {
    const hasInteracted = checkHasUserInteracted();
    if (
      isEmpty(appUserId) ||
      hasInteracted ||
      !isNil(waitingRoomData) ||
      !isScreenFocused
    ) {
      // Reset modal state when screen loses focus to prevent immediate showing on next focus
      if (!isScreenFocused) {
        setShouldShowWelcomeModal(false);
      }
      return;
    }

    // Add delay before showing modal (similar to existing onboarding logic)
    const timeoutId = setTimeout(() => {
      setShouldShowWelcomeModal(true);
    }, SmtWelcomeModalDelayMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [appUserId, waitingRoomData, checkHasUserInteracted, isScreenFocused]);

  // Hide SMT Welcome Modal if waiting room becomes active
  useEffect(() => {
    if (isNil(waitingRoomData) || !shouldShowWelcomeModal) return;
    setShouldShowWelcomeModal(false);
  }, [waitingRoomData, shouldShowWelcomeModal]);

  const handleWelcomeModalDismiss = () => {
    setShouldShowWelcomeModal(false);
    markUserAsInteracted();
  };

  // Don't show modal if:
  // - Screen is not focused (prevents showing on background tabs)
  // - Waiting room is active (prevents race condition)
  // - Native modals are locked (e.g., during deep link routing or biometric auth)
  const isSafeToShowModal =
    shouldShowWelcomeModal &&
    isScreenFocused &&
    isNil(waitingRoomData) &&
    isSafeToRenderNativeModals;

  return {
    handleWelcomeModalDismiss,
    isCheckingStoredValue,
    shouldShowWelcomeModal: isSafeToShowModal,
  };
};

export default useSMTWelcomeModal;
